import { create } from 'zustand';
import { loyaltyAPI } from '@matix/api-client';
import type { CustomerLoyalty, LoyaltyConfig } from '@matix/types';

/**
 * Loyalty state for the till.
 *
 * Two things live here:
 *   - `config`, loaded once on app boot like the tax config. It tells the UI
 *     whether to show anything at all, and what one point is worth.
 *   - `customer`, the standing of whoever is attached to the cart right now.
 *
 * Nothing here decides what a redemption costs — every value shown to the
 * cashier comes from `POST /loyalty/quote`, and the server re-validates it
 * again under lock when the order is charged.
 */

const DISABLED: LoyaltyConfig = {
  enabled: false,
  branch_eligible: false,
  earn_rate_points: 0,
  redeem_rate_value: 0,
  min_redeem_points: 0,
  max_redeem_percent: null,
  points_expiry_months: null,
  earn_on_tax: false,
  earn_on_delivery: false,
  earn_on_discounted: true,
};

interface LoyaltyState {
  config: LoyaltyConfig;
  configLoaded: boolean;
  /** Loyalty standing of the cart's current customer, or null for a walk-in. */
  customer: CustomerLoyalty | null;
  loadingCustomer: boolean;

  loadConfig: () => Promise<void>;
  /** Fetch (or clear, when passed null) the attached customer's standing. */
  loadCustomer: (customerId: number | null) => Promise<void>;
  /** Re-read the current customer after a charge so the balance shown is live. */
  refreshCustomer: () => Promise<void>;
  /** True when redemption can be offered at all on this till. */
  canRedeem: () => boolean;
}

export const useLoyalty = create<LoyaltyState>((set, get) => ({
  config: DISABLED,
  configLoaded: false,
  customer: null,
  loadingCustomer: false,

  loadConfig: async () => {
    try {
      const config = await loyaltyAPI.config();
      set({ config, configLoaded: true });
    } catch {
      // A tenant without the loyalty schema (or an older API) simply has no
      // programme. Failing quietly keeps the till usable — loyalty is an
      // addition to the sale, never a precondition for making one.
      set({ config: DISABLED, configLoaded: true });
    }
  },

  loadCustomer: async (customerId) => {
    if (customerId == null || !get().config.enabled) {
      set({ customer: null, loadingCustomer: false });
      return;
    }
    set({ loadingCustomer: true });
    try {
      const customer = await loyaltyAPI.forCustomer(customerId);
      set({ customer, loadingCustomer: false });
    } catch {
      set({ customer: null, loadingCustomer: false });
    }
  },

  refreshCustomer: async () => {
    const current = get().customer;
    if (!current) return;
    await get().loadCustomer(current.customer_id);
  },

  canRedeem: () => {
    const { config } = get();
    return config.enabled && config.branch_eligible && config.redeem_rate_value > 0;
  },
}));
